import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLiveOrders } from '../hooks/useLiveOrders';
import type { LiveOrder } from '../hooks/useLiveOrders';
import { nextOrderState } from '../hooks/useTableDetail';
import { supabase } from '../services/supabase';

function relativeMin(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000 / 60));
}

const KDS_COLUMNS: { id: 'incoming' | 'preparing' | 'ready'; label: string; states: string[] }[] = [
  { id: 'incoming', label: 'À PRÉPARER', states: ['pending', 'confirmed'] },
  { id: 'preparing', label: 'EN PRÉPARATION', states: ['preparing'] },
  { id: 'ready', label: 'PRÊT', states: ['ready'] },
];

async function advanceState(orderId: string, newState: string): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('tapnpay-order-advance-state', {
      body: { order_id: orderId, new_state: newState },
    });
    if (error) throw error;
  } catch {
    await supabase.from('orders').update({ state: newState }).eq('id', orderId);
    try {
      await supabase.from('guest_orders').update({ state: newState }).eq('id', orderId);
    } catch {
      // ignore — table might not exist or row not present
    }
  }
}

function KdsCard({
  order,
  isReady,
  onAdvance,
  busy,
}: {
  order: LiveOrder;
  isReady: boolean;
  onAdvance: () => void;
  busy: boolean;
}) {
  const age = relativeMin(order.created_at);
  // SLA : <5 min OK · 5-10 min orange · >10 min rouge (ticket en retard)
  const tier = age >= 10 ? 'red' : age >= 5 ? 'amber' : 'ok';
  const borderColor = isReady
    ? '1.5px solid #10B981'
    : tier === 'red'
      ? '2px solid #EF4444'
      : tier === 'amber'
        ? '2px solid #F59E0B'
        : '0.5px solid #000';
  const ageColor = tier === 'red' ? 'text-error' : tier === 'amber' ? 'text-amber-600' : 'text-on-surface-variant';
  return (
    <div
      className="bg-surface p-3"
      style={{
        border: borderColor,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold text-on-surface-strong">
          {order.table_number != null ? `Table ${order.table_number}` : 'À emporter'}
        </span>
        <span className={`text-[11px] uppercase tracking-wider tabular-nums font-bold ${ageColor}`}>
          il y a {age} min
        </span>
      </div>
      <div className="space-y-1 mb-3">
        {(order.items ?? []).map((it, i) => (
          <div key={i} className="text-sm text-on-surface">
            <span className="font-semibold tabular-nums">{it.qty}×</span> {it.name}
            {it.notes ? (
              <div className="text-[11px] text-on-surface-variant italic">↳ {it.notes}</div>
            ) : null}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onAdvance}
        disabled={busy}
        className="w-full min-h-[56px] flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider text-on-primary bg-primary disabled:opacity-50"
      >
        {busy ? '…' : (
          <>
            <span>AVANCER</span>
            <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
          </>
        )}
      </button>
    </div>
  );
}

export default function KitchenDisplayPage() {
  const { session } = useAuth();
  const { orders } = useLiveOrders(session?.storeId ?? '');
  const [busyId, setBusyId] = useState<string | null>(null);
  // Tick toutes les 30s pour rafraîchir les minuteurs d'ancienneté (SLA).
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // All-day counts : total par plat sur les commandes en file (hors prêtes).
  const allDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of orders) {
      const st = (o.state ?? o.status ?? 'pending').toLowerCase();
      if (st === 'ready' || st === 'completed' || st === 'served') continue;
      for (const it of o.items ?? []) m.set(it.name, (m.get(it.name) ?? 0) + (it.qty ?? 1));
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [orders]);

  const columns = useMemo(() => {
    return KDS_COLUMNS.map((col) => ({
      ...col,
      orders: orders
        .filter((o) => col.states.includes((o.state ?? o.status ?? 'pending').toLowerCase()))
        .sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        ),
    }));
  }, [orders]);

  const handleAdvance = useCallback(async (order: LiveOrder) => {
    const ns = nextOrderState(order.state ?? order.status ?? 'pending');
    if (!ns) return;
    setBusyId(order.id);
    try {
      await advanceState(order.id, ns);
    } finally {
      setBusyId(null);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-6 h-14"
        style={{ borderBottom: '0.5px solid #000' }}
      >
        <p className="text-base font-bold uppercase tracking-wider text-on-surface-strong">
          Écran cuisine
        </p>
        <span className="text-xs text-on-surface-variant tabular-nums">
          {orders.length} actives
        </span>
      </header>

      {/* All-day counts : combien de chaque plat en file (vue d'ensemble cuisine) */}
      {allDay.length > 0 && (
        <div
          className="bg-surface px-4 py-2 flex items-center gap-3 overflow-x-auto"
          style={{ borderBottom: '0.5px solid #000' }}
        >
          <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant shrink-0">
            En file
          </span>
          {allDay.map(([name, qty]) => (
            <span key={name} className="text-xs whitespace-nowrap text-on-surface-strong shrink-0">
              <span className="font-bold tabular-nums">{qty}×</span> {name}
            </span>
          ))}
        </div>
      )}

      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-px bg-on-surface-variant">
        {columns.map((col) => (
          <section key={col.id} className="bg-background flex flex-col">
            <div
              className="px-4 py-2 bg-surface flex items-center justify-between"
              style={{ borderBottom: '0.5px solid #000' }}
            >
              <span className="text-xs font-bold uppercase tracking-wider text-on-surface-strong">
                {col.label}
              </span>
              <span className="text-xs text-on-surface-variant tabular-nums">
                {col.orders.length}
              </span>
            </div>
            <div className="flex-1 p-3 grid gap-3 overflow-y-auto">
              {col.orders.length === 0 ? (
                <div className="text-center text-xs text-on-surface-variant py-8">
                  —
                </div>
              ) : (
                col.orders.map((o) => (
                  <KdsCard
                    key={o.id}
                    order={o}
                    isReady={col.id === 'ready'}
                    onAdvance={() => void handleAdvance(o)}
                    busy={busyId === o.id}
                  />
                ))
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
